import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Button } from 'reactstrap';
import classnames from 'classnames';
import entityComposition from '../common/classes/entityComposition';
import { processAutoLogoutDuration, processAutoLogoutAlertThreshold } from '../../utils/autoLogoutConfig';
const LogoutCounter = ({
  lastActionTimestamp: _lastActionTimestamp = null,
  user: _user = null,
  loginActions: _loginActions = null,
  autoLogoutDuration: _autoLogoutDuration = null,
  autoLogoutAlertThreshold: _autoLogoutAlertThreshold = null
}) => {
  const [timeRemaining, setTimeRemaining] = useState(0);
  const intervalRef = useRef(null);
  const logoutTriggeredRef = useRef(false);
  useEffect(() => {
    // Reset logout triggered flag when dependencies change
    logoutTriggeredRef.current = false;

    // Only show counter if user has auto-logout enabled
    const hasRequiredProps = (_user === null || _user === void 0 ? void 0 : _user.logoutAfterIdleTime) && _lastActionTimestamp && _loginActions && _autoLogoutDuration;
    if (!hasRequiredProps) {
      setTimeRemaining(0);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return undefined;
    }
    const calculateTimeRemaining = () => {
      const elapsed = Date.now() - _lastActionTimestamp;
      const remaining = Math.max(0, _autoLogoutDuration - elapsed);
      setTimeRemaining(remaining);

      // If time has expired, trigger logout immediately
      // This ensures logout happens even if the setTimeout callback doesn't fire
      if (remaining === 0 && _loginActions && !logoutTriggeredRef.current) {
        logoutTriggeredRef.current = true;
        _loginActions.logoutUser();
      }
    };

    // Calculate immediately
    calculateTimeRemaining();

    // Update every second
    intervalRef.current = setInterval(calculateTimeRemaining, 1000);
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [_lastActionTimestamp, _user === null || _user === void 0 ? void 0 : _user.logoutAfterIdleTime, _loginActions, _autoLogoutDuration]);

  // Don't render if user doesn't have auto-logout enabled, no time remaining,
  // or more than threshold left
  if (!(_user !== null && _user !== void 0 && _user.logoutAfterIdleTime) || timeRemaining <= 0 || !_autoLogoutAlertThreshold || timeRemaining > _autoLogoutAlertThreshold) {
    return null;
  }
  const formatTime = milliseconds => {
    const totalSeconds = Math.floor(milliseconds / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };
  const formattedTime = formatTime(timeRemaining);
  const handleStayOnline = () => {
    // Dispatch keepAlive to reset the timer (this will update lastAction.timestamp)
    _loginActions.keepAlive();
  };
  const handleLogout = () => {
    // Trigger logout with auto-save of pending changes
    _loginActions.logoutUserWithSave();
  };
  return /*#__PURE__*/React.createElement("div", {
    className: "tqc-toast-container t-toast-container"
  }, /*#__PURE__*/React.createElement("div", {
    className: "tqc-toast-notification t-toast-notification"
  }, /*#__PURE__*/React.createElement("div", {
    className: "tqc-toast-content"
  }, /*#__PURE__*/React.createElement("div", {
    className: "t-notification-general"
  }, /*#__PURE__*/React.createElement("span", {
    className: classnames('tqc-notification-info-header', 't-notification-header')
  }, "Are you still there?"), /*#__PURE__*/React.createElement("br", null), "You will be automatically logged out due to inactivity.", /*#__PURE__*/React.createElement("br", null), `Remaining: ${formattedTime}`, /*#__PURE__*/React.createElement("br", null), /*#__PURE__*/React.createElement("br", null), /*#__PURE__*/React.createElement(Button, {
    color: "primary",
    onClick: handleStayOnline,
    className: classnames('t-notification-button-left', 't-nbl-label-stay-online')
  }, "Stay Online"), /*#__PURE__*/React.createElement(Button, {
    color: "primary",
    style: {
      float: 'right'
    },
    onClick: handleLogout,
    className: classnames('t-notification-button-right', 't-nbr-label-logout')
  }, "Save & Logout")))));
};
LogoutCounter.propTypes = {
  lastActionTimestamp: PropTypes.number,
  user: PropTypes.shape({
    logoutAfterIdleTime: PropTypes.bool
  }),
  loginActions: PropTypes.shape({
    logoutUser: PropTypes.func,
    logoutUserWithSave: PropTypes.func,
    keepAlive: PropTypes.func
  }),
  autoLogoutDuration: PropTypes.number,
  autoLogoutAlertThreshold: PropTypes.number
};
export default entityComposition(LogoutCounter, ['settings'], {
  lastActionTimestamp: state => {
    var _state$lastAction;
    return (_state$lastAction = state.lastAction) === null || _state$lastAction === void 0 ? void 0 : _state$lastAction.timestamp;
  },
  user: state => state.login.user,
  autoLogoutDuration: ({
    settings: _settings = {}
  }) => {
    var _settings$settings;
    const rawValue = (_settings$settings = _settings.settings) === null || _settings$settings === void 0 || (_settings$settings = _settings$settings.configuration) === null || _settings$settings === void 0 || (_settings$settings = _settings$settings.logoutProperties) === null || _settings$settings === void 0 ? void 0 : _settings$settings.autoLogoutDuration;
    return processAutoLogoutDuration(rawValue);
  },
  autoLogoutAlertThreshold: ({
    settings: _settings2 = {}
  }) => {
    var _settings2$settings;
    const rawValue = (_settings2$settings = _settings2.settings) === null || _settings2$settings === void 0 || (_settings2$settings = _settings2$settings.configuration) === null || _settings2$settings === void 0 || (_settings2$settings = _settings2$settings.logoutProperties) === null || _settings2$settings === void 0 ? void 0 : _settings2$settings.autoLogoutAlertThreshold;
    return processAutoLogoutAlertThreshold(rawValue);
  }
}, ['login']);